import React, { useState, useEffect, useRef, useMemo } from 'react';
import { db, functions } from '../firebase';
import { useNavigate } from 'react-router-dom';

/* ─────────────────────────────────────────────
   TYPE CONFIG  — colour palette, icon, label
───────────────────────────────────────────── */
const TYPE_CONFIG = {
  order: {
    label: 'Order',
    filterLabel: 'Order',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
      </svg>
    ),
    gradient: 'linear-gradient(135deg, #1a0a00 0%, #3d1f00 100%)',
    badge: '#c97b1e',
    badgeBg: 'rgba(201,123,30,0.15)',
    accent: '#e8a040',
    glow: 'rgba(201,123,30,0.18)',
  },
  general: {
    label: 'General Enquiry',
    filterLabel: 'General',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
    gradient: 'linear-gradient(135deg, #0a1a0a 0%, #0d3320 100%)',
    badge: '#22c55e',
    badgeBg: 'rgba(34,197,94,0.15)',
    accent: '#4ade80',
    glow: 'rgba(34,197,94,0.18)',
  },
  getQuotation: {
    label: 'Quotation Request',
    filterLabel: 'Quotation',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8m-4-4v4"/>
      </svg>
    ),
    gradient: 'linear-gradient(135deg, #1a0a1a 0%, #3d1045 100%)',
    badge: '#a855f7',
    badgeBg: 'rgba(168,85,247,0.15)',
    accent: '#c084fc',
    glow: 'rgba(168,85,247,0.18)',
  },
  appointment: {
    label: 'Campus / Stall Appointment',
    filterLabel: 'Appointment',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
    gradient: 'linear-gradient(135deg, #001433 0%, #002966 100%)',
    badge: '#3b82f6',
    badgeBg: 'rgba(59,130,246,0.15)',
    accent: '#60a5fa',
    glow: 'rgba(59,130,246,0.18)',
  },
  wedding: {
    label: 'Wedding Outfit Planning',
    filterLabel: 'Wedding',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    ),
    gradient: 'linear-gradient(135deg, #1a0a12 0%, #3d0020 100%)',
    badge: '#f43f5e',
    badgeBg: 'rgba(244,63,94,0.15)',
    accent: '#fb7185',
    glow: 'rgba(244,63,94,0.18)',
  },
  business: {
    label: 'Uniform Quotation',
    filterLabel: 'Uniform Quot.',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/>
      </svg>
    ),
    gradient: 'linear-gradient(135deg, #0a0f1a 0%, #101e3d 100%)',
    badge: '#0ea5e9',
    badgeBg: 'rgba(14,165,233,0.15)',
    accent: '#38bdf8',
    glow: 'rgba(14,165,233,0.18)',
  },
  franchise: {
    label: 'Smart Kiosk Franchise',
    filterLabel: 'Franchise',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
    ),
    gradient: 'linear-gradient(135deg, #1a1200 0%, #3d2d00 100%)',
    badge: '#eab308',
    badgeBg: 'rgba(234,179,8,0.15)',
    accent: '#facc15',
    glow: 'rgba(234,179,8,0.18)',
  },
  default: {
    label: 'Notification',
    filterLabel: 'Other',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
    ),
    gradient: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
    badge: '#a8a8a8',
    badgeBg: 'rgba(168,168,168,0.15)',
    accent: '#c8c8c8',
    glow: 'rgba(168,168,168,0.18)',
  },
};

// All filter types in order (All first, then each form type, then Orders)
const FILTER_TYPES = ['all', 'order', 'general', 'appointment', 'wedding', 'business', 'franchise'];
const READ_FILTERS = [
  { key: 'all',    label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read',   label: 'Read' },
  { key: 'pending', label: 'Needs reply' },
  { key: 'replied', label: 'Replied' },
];


/* ─────────────────────────────────────────────
   FIELD LABELS  —  raw key → human label
───────────────────────────────────────────── */
const FIELD_LABELS = {
  name:         { label: 'Full Name',          icon: '👤' },
  email:        { label: 'Email Address',       icon: '✉️' },
  phone:        { label: 'Phone Number',        icon: '📞' },
  city:         { label: 'City / Location',     icon: '📍' },
  organization: { label: 'Organisation',        icon: '🏢' },
  orders:       { label: 'Quantity / Count',    icon: '📋' },
  date:         { label: 'Preferred Date',      icon: '📅' },
  subject:      { label: 'Issue Type',          icon: '🏷️' },
  issueType:    { label: 'Issue Code',          icon: '🏷️' },
  formType:     { label: 'Form Type',           icon: '🧾' },
  orderNo:      { label: 'Order Number',        icon: '📦' },
  designation:  { label: 'Designation',         icon: '🪪' },
  eventName:    { label: 'Event Name',          icon: '🎪' },
  campusName:   { label: 'Campus Name',         icon: '🏫' },
};

const DETAIL_KEY_ORDER = [
  'formType', 'subject', 'issueType', 'orderNo', 'name', 'email', 'phone',
  'organization', 'campusName', 'designation', 'eventName', 'city', 'date', 'orders',
];

const DETAIL_MESSAGE_KEYS = ['message', 'request', 'des'];

const ISSUE_TYPE_LABELS = {
  order_status: 'Order Status',
  delivery: 'Delivery / Shipping Issue',
  size_fit: 'Size / Fit Problem',
  quality: 'Product Quality',
  return_exchange: 'Return / Exchange',
  payment: 'Payment Issue',
  tracking: 'Order Tracking',
  customization: 'Customization Query',
  general_query: 'General Question',
  other: 'Other',
};

const FORM_TYPE_LABELS = {
  general: 'General Enquiry',
  getQuotation: 'Quotation Request',
  appointment: 'Campus / Stall Appointment',
  wedding: 'Wedding Outfit Planning',
  business: 'Uniform Quotation',
  franchise: 'Smart Kiosk Franchise',
  design_photo: 'Design Photo',
};

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const toDate = (ts) => {
  if (!ts) return null;
  try { return ts.toDate ? ts.toDate() : new Date(ts); } catch { return null; }
};

const formatTime = (ts) => {
  const d = toDate(ts);
  if (!d) return 'Just now';
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60)   return 'Just now';
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

/* Bucket label for date grouping */
const getDateBucket = (ts) => {
  const d = toDate(ts);
  if (!d) return 'Earlier';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday); startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfWeek = new Date(startOfToday); startOfWeek.setDate(startOfWeek.getDate() - 6);
  if (d >= startOfToday)      return 'Today';
  if (d >= startOfYesterday)  return 'Yesterday';
  if (d >= startOfWeek)       return 'Earlier this week';
  return 'Older';
};

const BUCKET_ORDER = ['Today', 'Yesterday', 'Earlier this week', 'Older'];

/* Subtle ding sound via WebAudio — no asset required */
const playDing = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.18);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
    o.connect(g); g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.28);
  } catch { /* ignore */ }
};

const getParsedDetails = (notif) => {
  // Prefer structured object from Firestore
  if (notif.details && Object.keys(notif.details).length > 0) return notif.details;

  // Fallback: parse legacy pipe-separated string
  const msg = notif.message;
  if (msg && msg.includes('|')) {
    const details = {};
    msg.split('|').forEach(part => {
      const idx = part.indexOf(':');
      if (idx !== -1) {
        const k = part.substring(0, idx).trim().toLowerCase();
        const v = part.substring(idx + 1).trim().replace(/^"|"$/g, '');
        details[k] = v;
      }
    });
    return details;
  }
  return null;
};

const getMessageText = (details, notif) => {
  return details?.message || details?.request || details?.des || notif.message || '';
};

const formatDetailValue = (key, value) => {
  if (key === 'issueType') return ISSUE_TYPE_LABELS[value] || value;
  if (key === 'formType') return FORM_TYPE_LABELS[value] || value;
  return value;
};

const sortDetailEntries = (entries) => {
  return [...entries].sort(([keyA], [keyB]) => {
    const indexA = DETAIL_KEY_ORDER.indexOf(keyA);
    const indexB = DETAIL_KEY_ORDER.indexOf(keyB);
    if (indexA === -1 && indexB === -1) return keyA.localeCompare(keyB);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
};

const getDetailEntries = (details) => {
  if (!details) return [];
  return sortDetailEntries(
    Object.entries(details).filter(([key, value]) => !DETAIL_MESSAGE_KEYS.includes(key) && value)
  );
};

const getPreviewText = (text, max = 120) => {
  if (!text) return 'Open notification to inspect the full details.';
  const clean = text.replace(/^"|"$/g, '').trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max).trim()}...`;
};

const getTopFields = (details) => {
  return getDetailEntries(details).filter(([key]) => key !== 'formType').slice(0, 3);
};

/* ─────────────────────────────────────────────
   NOTIFICATION CARD COMPONENT
───────────────────────────────────────────── */
function NotificationDetailModal({ notif, onClose, onNavigate, onSendReply }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [replyError, setReplyError] = useState('');
  if (!notif) return null;

  const cfg = TYPE_CONFIG[notif.type] || TYPE_CONFIG.default;
  const details = getParsedDetails(notif);
  const messageText = getMessageText(details, notif);
  const recipientEmail = details?.email || '';
  const subjectValue = replySubject || `Re: ${notif.title || cfg.label}`;
  const formRows = getDetailEntries(details);

  const handleSendReply = async () => {
    setReplyError('');
    if (!recipientEmail) {
      setReplyError('Customer email available nahi hai.');
      return;
    }
    if (!replyMessage.trim()) {
      setReplyError('A reply message is required.');
      return;
    }

    setSendingReply(true);
    try {
      await onSendReply(notif, {
        to: recipientEmail,
        subject: subjectValue,
        message: replyMessage.trim(),
      });
      setReplyOpen(false);
      setReplyMessage('');
      setReplySubject('');
    } catch (error) {
      setReplyError(error?.message || 'Could not send the reply. Please try again.');
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(20,17,13,0.62)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(860px, 100%)',
          maxHeight: '88vh',
          overflow: 'auto',
          background: 'linear-gradient(180deg, #fffdf8 0%, #f8f1e5 100%)',
          border: '1px solid #D7DBDF',
          borderRadius: 7,
          boxShadow: '0 30px 90px rgba(0,0,0,0.32)',
        }}
      >
        <div style={{
          background: cfg.gradient,
          color: '#fff',
          padding: '22px 24px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 16,
          position: 'sticky',
          top: 0,
          zIndex: 2,
        }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: 8,
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: cfg.accent,
            flexShrink: 0,
          }}>
            {cfg.icon}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
              <span style={{
                padding: '5px 10px',
                borderRadius: 6,
                background: 'rgba(255,255,255,0.12)',
                border: '1px solid rgba(255,255,255,0.16)',
                color: cfg.accent,
                fontSize: '0.7rem',
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}>
                {cfg.label}
              </span>
              <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.66)', fontWeight: 600 }}>
                {formatTime(notif.timestamp)}
              </span>
            </div>
            <h3 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800, lineHeight: 1.25 }}>
              {notif.title || 'Notification details'}
            </h3>
          </div>
          <button
            onClick={onClose}
            title="Close"
            style={{
              width: 38,
              height: 38,
              borderRadius: 7,
              border: '1px solid rgba(255,255,255,0.18)',
              background: 'rgba(255,255,255,0.08)',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '1.2rem',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 24 }}>
          {notif.replied && (
            <div style={{
              display: 'flex',
              gap: 12,
              alignItems: 'flex-start',
              background: '#eefaf3',
              border: '1px solid #cdebd8',
              borderRadius: 8,
              padding: '13px 15px',
              marginBottom: 18,
              color: '#245f41',
            }}>
              <div style={{ width: 30, height: 30, borderRadius: 7, background: '#d9f4e4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900 }}>
                ✓
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '0.76rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>
                  Reply sent
                </div>
                <div style={{ fontSize: '0.88rem', lineHeight: 1.5 }}>
                  {notif.replySubject || 'Customer ko reply send ho chuka hai.'}
                  {notif.replyTo ? ` • ${notif.replyTo}` : ''}
                </div>
              </div>
            </div>
          )}

          {formRows.length > 0 && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 12,
              marginBottom: messageText ? 18 : 0,
            }}>
              {formRows.map(([key, value]) => {
                const meta = FIELD_LABELS[key] || { label: key.replace(/_/g, ' '), icon: '•' };
                return (
                  <div key={key} style={{
                    background: '#FFFFFF',
                    border: '1px solid #D7DBDF',
                    borderRadius: 8,
                    padding: '14px 15px',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 7,
                      marginBottom: 7,
                      color: '#6A7079',
                      fontSize: '0.66rem',
                      fontWeight: 800,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}>
                      <span>{meta.icon}</span>
                      {meta.label}
                    </div>
                    <div style={{ color: '#1e160d', fontSize: '0.94rem', fontWeight: 650, lineHeight: 1.5, wordBreak: 'break-word' }}>
                      {String(formatDetailValue(key, value))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {messageText && (
            <div style={{
              background: 'linear-gradient(135deg, #fff 0%, #f7eddd 100%)',
              border: '1px solid #e7d6bf',
              borderLeft: `5px solid ${cfg.accent === '#e8a040' ? '#d4af37' : cfg.accent}`,
              borderRadius: '0 18px 18px 0',
              padding: '16px 18px',
              marginBottom: 18,
            }}>
              <div style={{ color: '#6A7079', fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 9 }}>
                Message / Requirement
              </div>
              <div style={{ color: '#2c1e0a', fontSize: '0.96rem', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                {messageText.replace(/^"|"$/g, '')}
              </div>
            </div>
          )}

          {!details && !messageText && (
            <div style={{ color: '#7a6750', fontSize: '0.92rem', lineHeight: 1.6 }}>
              Is notification mein form details available nahi hain.
            </div>
          )}

          {replyOpen && (
            <div style={{
              marginBottom: 18,
              background: '#FFFFFF',
              border: '1px solid #D7DBDF',
              borderRadius: 8,
              padding: 16,
            }}>
              <div style={{ color: '#6A7079', fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>
                Reply from connect@bluepearlindustries.com
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#8c7558', fontWeight: 700, marginBottom: 5 }}>To</div>
                  <input
                    value={recipientEmail || 'No customer email found'}
                    disabled
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 7, border: '1px solid #D7DBDF', background: '#F7F8F9', color: '#5d4a34' }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: '#8c7558', fontWeight: 700, marginBottom: 5 }}>Subject</div>
                  <input
                    value={subjectValue}
                    onChange={(e) => setReplySubject(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 7, border: '1px solid #D7DBDF', background: '#fff', color: '#1e160d' }}
                  />
                </div>
              </div>
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder={`Hello${details?.name ? ` ${details.name}` : ''},\n\nThank you for contacting MAVIINCI.\n`}
                rows={7}
                style={{ width: '100%', resize: 'vertical', padding: 12, borderRadius: 7, border: '1px solid #D7DBDF', background: '#fff', color: '#1e160d', lineHeight: 1.55 }}
              />
              {replyError && (
                <div style={{ marginTop: 8, color: '#c0392b', fontSize: '0.82rem', fontWeight: 700 }}>
                  {replyError}
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button
                  onClick={() => setReplyOpen(false)}
                  disabled={sendingReply}
                  style={{ background: '#EEF0F2', border: '1px solid #dfcfb8', borderRadius: 7, padding: '9px 13px', color: '#6d5a40', fontWeight: 700, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendReply}
                  disabled={sendingReply || !recipientEmail}
                  style={{ background: '#201507', border: '1px solid #201507', borderRadius: 7, padding: '9px 13px', color: '#fff', fontWeight: 800, cursor: sendingReply || !recipientEmail ? 'not-allowed' : 'pointer', opacity: sendingReply || !recipientEmail ? 0.65 : 1 }}
                >
                  {sendingReply ? 'Sending...' : 'Send reply'}
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', paddingTop: 4 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={onClose}
                style={{
                  background: '#EEF0F2',
                  border: '1px solid #dfcfb8',
                  borderRadius: 7,
                  padding: '11px 16px',
                  color: '#6d5a40',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
              <button
                onClick={() => setReplyOpen((value) => !value)}
                style={{
                  background: replyOpen ? '#EEF0F2' : '#201507',
                  border: replyOpen ? '1px solid #dfcfb8' : '1px solid #201507',
                  borderRadius: 7,
                  padding: '11px 16px',
                  color: replyOpen ? '#6d5a40' : '#fff',
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                Reply
              </button>
            </div>
            <div>
              {notif.link && (
              <button
                onClick={() => onNavigate(notif)}
                style={{
                  background: cfg.gradient,
                  border: 'none',
                  borderRadius: 7,
                  padding: '11px 16px',
                  color: '#fff',
                  fontWeight: 800,
                  cursor: 'pointer',
                  boxShadow: `0 12px 22px ${cfg.glow}`,
                }}
              >
                Open linked page
              </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color, accent }) {
  return (
    <div style={{
      position: 'relative',
      overflow: 'hidden',
      minWidth: 0,
      borderRadius: 8,
      border: '1px solid rgba(227, 216, 198, 0.9)',
      background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(251,247,240,0.92) 100%)',
      padding: '14px 14px 13px',
      boxShadow: '0 10px 24px rgba(45, 32, 14, 0.06)',
    }}>
      <div style={{
        position: 'absolute',
        inset: '0 auto auto 0',
        width: '100%',
        height: 3,
        background: accent,
        opacity: 0.9,
      }} />
      <div style={{ fontSize: '1.22rem', fontWeight: 900, color, lineHeight: 1.05 }}>{value}</div>
      <div style={{ marginTop: 7, fontSize: '0.64rem', color: '#6A7079', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
    </div>
  );
}

function NotificationCard({ notif, onOpenDetails, onDelete, onToggleRead }) {
  const [hovered, setHovered] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const cfg = TYPE_CONFIG[notif.type] || TYPE_CONFIG.default;
  const details = getParsedDetails(notif);
  const messageText = getMessageText(details, notif);
  const previewText = getPreviewText(messageText, 150);
  const topFields = getTopFields(details);
  const customerName = details?.name || details?.email || 'Customer';
  const isReplied = Boolean(notif.replied);
  const unreadBorder = !notif.read ? cfg.accent : '#D7DBDF';

  const handleDelete = async (e) => {
    e.stopPropagation();
    setDeleting(true);
    await onDelete(notif.id);
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onOpenDetails(notif)}
      style={{
        borderRadius: 7,
        overflow: 'hidden',
        background: hovered ? 'linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 100%)' : 'linear-gradient(180deg, #ffffff 0%, #FFFFFF 100%)',
        border: `1px solid ${hovered ? cfg.accent : unreadBorder}`,
        boxShadow: hovered
          ? `0 24px 44px ${cfg.glow}`
          : '0 10px 24px rgba(31,22,10,0.05)',
        transition: 'background 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease',
        opacity: deleting ? 0.45 : 1,
        position: 'relative',
        cursor: 'pointer',
      }}
    >
      <div style={{ position: 'absolute', inset: '0 auto 0 0', width: 5, background: hovered ? cfg.accent : (!notif.read ? cfg.accent : '#D7DBDF') }} />
      <div style={{ position: 'absolute', inset: '0 0 auto 0', height: 1, background: 'linear-gradient(90deg, rgba(255,255,255,0.7), transparent)' }} />

      <div style={{ padding: '18px 18px 18px 20px', position: 'relative' }}>
        <div className="ncard-grid" style={{ display: 'grid', gridTemplateColumns: '48px minmax(0, 1fr) auto', gap: 16, alignItems: 'start' }}>
          <div style={{
            width: 46,
            height: 46,
            borderRadius: 8,
            background: cfg.gradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff8e9',
            flexShrink: 0,
            boxShadow: `0 14px 28px ${cfg.glow}`,
          }}>
            {cfg.icon}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 9 }}>
              <span style={{ fontSize: '0.7rem', color: '#8b6f42', fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                {cfg.label}
              </span>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#d8c39f' }} />
              <span style={{ fontSize: '0.74rem', color: '#6A7079', fontWeight: 600 }}>
                {formatTime(notif.timestamp)}
              </span>
              {!notif.read && (
                <span style={{ padding: '4px 9px', borderRadius: 6, background: '#fff1d6', color: '#8a5a00', fontSize: '0.67rem', fontWeight: 900, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Unread
                </span>
              )}
              <span style={{
                padding: '4px 9px',
                borderRadius: 6,
                background: isReplied ? '#e8f7ee' : '#fff4e5',
                color: isReplied ? '#187044' : '#9a5b00',
                fontSize: '0.67rem',
                fontWeight: 900,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}>
                {isReplied ? 'Replied' : 'Needs reply'}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, minWidth: 0, marginBottom: 7, flexWrap: 'wrap' }}>
              <div style={{ fontSize: '1.02rem', fontWeight: notif.read ? 800 : 900, color: '#17110a', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {customerName}
              </div>
              <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#5d4a34', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '4px 9px', borderRadius: 6, background: cfg.badgeBg, border: `1px solid ${cfg.glow}` }}>
                {notif.title || cfg.label}
              </div>
            </div>

            <div style={{ fontSize: '0.85rem', color: '#6e614f', lineHeight: 1.55, maxWidth: '92%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {previewText}
            </div>

            {topFields.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 13 }}>
                {topFields.map(([key, value]) => {
                  const meta = FIELD_LABELS[key] || { label: key, icon: '' };
                  return (
                    <span key={key} style={{ fontSize: '0.72rem', color: '#67543b', background: '#f8f1e7', border: '1px solid #D7DBDF', borderRadius: 6, padding: '5px 10px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.65)' }}>
                      <b style={{ color: '#6A7079' }}>{meta.label}:</b> {formatDetailValue(key, value)}
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          <div className="ncard-actions" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 210 }}>
            <button
              onClick={(e) => { e.stopPropagation(); onToggleRead(notif); }}
              title={notif.read ? 'Mark as unread' : 'Mark as read'}
              style={{
                background: '#faf7f1',
                border: '1px solid #D7DBDF',
                borderRadius: 7,
                padding: '9px 11px',
                cursor: 'pointer',
                color: '#80643d',
                fontSize: '0.74rem',
                fontWeight: 900,
                whiteSpace: 'nowrap',
              }}
            >
              {notif.read ? 'Unread' : 'Read'}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onOpenDetails(notif); }}
              style={{
                background: cfg.gradient,
                color: '#fff',
                border: 'none',
                borderRadius: 7,
                padding: '9px 13px',
                fontSize: '0.74rem',
                fontWeight: 900,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                boxShadow: `0 12px 24px ${cfg.glow}`,
              }}
            >
              Open
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              title="Delete notification"
              style={{
                background: '#fff',
                border: '1px solid #f0cfca',
                borderRadius: 7,
                padding: '9px 11px',
                cursor: deleting ? 'not-allowed' : 'pointer',
                color: '#b33b2f',
                fontSize: '0.74rem',
                fontWeight: 900,
              }}
            >
              Delete
            </button>
          </div>
        </div>

        {isReplied && notif.replySubject && (
          <div style={{ margin: '14px 0 0 64px', padding: '10px 12px', borderRadius: 7, background: 'linear-gradient(180deg, #f0faf4 0%, #eef8f2 100%)', border: '1px solid #cdebd8', color: '#276344', fontSize: '0.76rem', lineHeight: 1.45, boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55)' }}>
            Reply sent: <b>{notif.replySubject}</b>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────── */
function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [readFilter, setReadFilter] = useState('all'); // all | unread | read | pending | replied
  const [search, setSearch] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);
  const [selectedNotif, setSelectedNotif] = useState(null);
  const navigate = useNavigate();

  // Track previous newest doc id to detect arrivals (skip first snapshot)
  const firstSnapRef = useRef(true);
  const knownIdsRef = useRef(new Set());

  // Ask for browser notification permission once
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const unsubscribe = db.collection('Notifications')
      .orderBy('timestamp', 'desc')
      .limit(100)
      .onSnapshot(
        (snapshot) => {
          const list = [];
          snapshot.forEach((doc) => list.push({ id: doc.id, ...doc.data() }));

          // Detect newly added notifications (only after first snapshot)
          if (firstSnapRef.current) {
            firstSnapRef.current = false;
            knownIdsRef.current = new Set(list.map(n => n.id));
          } else {
            const fresh = list.filter(n => !knownIdsRef.current.has(n.id));
            if (fresh.length > 0) {
              playDing();
              const first = fresh[0];
              if ('Notification' in window && Notification.permission === 'granted') {
                try {
                  const n = new Notification(first.title || 'New notification', {
                    body: (TYPE_CONFIG[first.type] || TYPE_CONFIG.default).label,
                    tag: first.id,
                    silent: true,
                  });
                  n.onclick = () => { window.focus(); if (first.link) navigate(first.link); n.close(); };
                } catch { /* ignore */ }
              }
            }
            knownIdsRef.current = new Set(list.map(n => n.id));
          }

          setNotifications(list);
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching notifications:', error);
          setLoading(false);
        }
      );
    return () => unsubscribe();
  }, [navigate]);

  const handleOpenDetails = async (notif) => {
    try {
      if (!notif.read) {
        await db.collection('Notifications').doc(notif.id).update({ read: true });
      }
      setSelectedNotif({ ...notif, read: true });
    } catch (e) {
      console.error('Error opening notification details:', e);
      setSelectedNotif(notif);
    }
  };

  const handleNavigateFromDetails = (notif) => {
    setSelectedNotif(null);
    if (notif.link) navigate(notif.link);
  };

  const handleSendReply = async (notif, reply) => {
    const sendReplyEmail = functions.httpsCallable('sendReplyEmail');
    await sendReplyEmail({
      notificationId: notif.id,
      to: reply.to,
      subject: reply.subject,
      message: reply.message,
    });
    setSelectedNotif((current) => current ? {
      ...current,
      replied: true,
      replyTo: reply.to,
      replySubject: reply.subject,
      replyMessage: reply.message,
    } : current);
  };

  const handleDelete = async (id) => {
    try {
      await db.collection('Notifications').doc(id).delete();
    } catch (e) {
      console.error('Error deleting notification:', e);
    }
  };

  const handleToggleRead = async (notif) => {
    try {
      await db.collection('Notifications').doc(notif.id).update({ read: !notif.read });
    } catch (e) {
      console.error('Error toggling read:', e);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const batch = db.batch();
      const unread = notifications.filter(n => !n.read);
      if (!unread.length) return;
      unread.forEach(n => batch.update(db.collection('Notifications').doc(n.id), { read: true }));
      await batch.commit();
    } catch (e) {
      console.error('Error marking all as read:', e);
    }
  };

  const handleClearAll = async () => {
    try {
      const batch = db.batch();
      notifications.forEach(n => batch.delete(db.collection('Notifications').doc(n.id)));
      await batch.commit();
      setConfirmClear(false);
    } catch (e) {
      console.error('Error clearing all:', e);
    }
  };

  // Apply type + read-status + search filters
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notifications.filter(n => {
      if (filter !== 'all' && n.type !== filter) return false;
      if (readFilter === 'unread' && n.read)  return false;
      if (readFilter === 'read'   && !n.read) return false;
      if (readFilter === 'pending' && n.replied) return false;
      if (readFilter === 'replied' && !n.replied) return false;
      if (!q) return true;
      const haystack = [
        n.title, n.message,
        n.details && Object.values(n.details).join(' '),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [notifications, filter, readFilter, search]);

  // Group by date bucket
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach(n => {
      const b = getDateBucket(n.timestamp);
      if (!map.has(b)) map.set(b, []);
      map.get(b).push(n);
    });
    return BUCKET_ORDER.filter(b => map.has(b)).map(b => [b, map.get(b)]);
  }, [filtered]);

  const unreadCount = notifications.filter(n => !n.read).length;
  const repliedCount = notifications.filter(n => n.replied).length;
  const pendingReplyCount = notifications.filter(n => !n.replied).length;
  const todayCount = notifications.filter((n) => getDateBucket(n.timestamp) === 'Today').length;
  const activeTypeLabel = filter === 'all' ? 'All categories' : (TYPE_CONFIG[filter]?.filterLabel || TYPE_CONFIG[filter]?.label || 'Selected');
  const filteredOutCount = notifications.length - filtered.length;

  if (loading) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        minHeight: '60vh', gap: 16,
        color: '#6A7079',
      }}>
        <div style={{
          width: 44, height: 44,
          border: '3px solid #D7DBDF',
          borderTopColor: '#d4af37',
          borderRadius: '50%',
          animation: 'spin 0.9s linear infinite',
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <span style={{ fontSize: '0.9rem', opacity: 0.7 }}>Loading notifications…</span>
      </div>
    );
  }

  return (
    <div style={{
      padding: '24px 28px 40px',
      width: '100%',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
      minHeight: '100vh',
      background: 'radial-gradient(circle at top left, rgba(212,175,55,0.10), transparent 22%), radial-gradient(circle at top right, rgba(22,16,10,0.06), transparent 18%), linear-gradient(180deg, #EEF0F2 0%, #EEF0F2 100%)',
    }}>
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        background: 'rgba(246,242,234,0.8)',
        backdropFilter: 'blur(14px)',
        padding: '0 0 16px',
        marginBottom: 24,
      }}>
        <div style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.82) 0%, rgba(251,247,240,0.9) 100%)',
          border: '1px solid rgba(226,215,198,0.95)',
          borderRadius: 7,
          boxShadow: '0 18px 44px rgba(43, 29, 11, 0.08)',
          padding: '18px 18px 16px',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 18,
            marginBottom: 16,
            flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontSize: '0.7rem', color: '#6A7079', fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 6 }}>
                MAVIINCI Inbox
              </div>
              <h2 style={{ margin: 0, color: '#17110a', fontSize: '1.9rem', fontWeight: 900, lineHeight: 1.02 }}>
                Notifications
              </h2>
              <p style={{ margin: '8px 0 0', color: '#7b6d5a', fontSize: '0.88rem', lineHeight: 1.55, maxWidth: 560 }}>
                High-priority enquiries, open replies, and recent customer activity in one focused inbox.
              </p>
            </div>

            <div className="nstats-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(86px, 1fr))', gap: 10, flex: '0 1 620px' }}>
            {[
              { label: 'Total', value: notifications.length, color: '#17110a', accent: 'linear-gradient(90deg, #2b1d0b 0%, #8b6f42 100%)' },
              { label: 'Unread', value: unreadCount, color: '#9a5b00', accent: 'linear-gradient(90deg, #c68d2f 0%, #f0c87b 100%)' },
              { label: 'Need Reply', value: pendingReplyCount, color: '#b45309', accent: 'linear-gradient(90deg, #d97706 0%, #fdba74 100%)' },
              { label: 'Replied', value: repliedCount, color: '#187044', accent: 'linear-gradient(90deg, #187044 0%, #78d3a6 100%)' },
              { label: 'Today', value: todayCount, color: '#1d4f91', accent: 'linear-gradient(90deg, #1d4f91 0%, #8cb4ea 100%)' },
            ].map((item) => (
              <MetricCard key={item.label} label={item.label} value={item.value} color={item.color} accent={item.accent} />
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) auto auto', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <div style={{ position: 'relative', minWidth: 220 }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#b59a72', fontSize: '0.95rem', pointerEvents: 'none' }}>🔍</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, email, phone, message…"
              style={{ width: '100%', padding: '13px 38px 13px 38px', borderRadius: 8, border: '1px solid #ded2c1', background: 'rgba(255,255,255,0.92)', fontSize: '0.87rem', color: '#2c1e0a', outline: 'none', boxSizing: 'border-box', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.85)' }}
            />
            {search && (
              <button onClick={() => setSearch('')} title="Clear search" style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: '#b59a72', fontSize: '1rem', padding: '4px 8px' }}>
                ✕
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.92)', border: '1px solid #ded2c1', borderRadius: 8, padding: 4, overflowX: 'auto', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.8)' }}>
            {READ_FILTERS.map((item) => {
              const active = readFilter === item.key;
              const count = item.key === 'all'
                ? notifications.length
                : item.key === 'unread'
                ? unreadCount
                : item.key === 'read'
                ? notifications.length - unreadCount
                : item.key === 'pending'
                ? pendingReplyCount
                : repliedCount;
              return (
                <button key={item.key} onClick={() => setReadFilter(item.key)} style={{ background: active ? 'linear-gradient(135deg, #1c140c 0%, #352313 100%)' : 'transparent', color: active ? '#fff' : '#6f604e', border: 'none', borderRadius: 7, padding: '8px 11px', fontSize: '0.74rem', fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap', boxShadow: active ? '0 10px 20px rgba(34,23,11,0.16)' : 'none' }}>
                  {item.label}<span style={{ background: active ? 'rgba(255,255,255,0.2)' : '#e8ddd0', color: active ? '#fff' : '#6A7079', borderRadius: 6, padding: '1px 6px', fontSize: '0.68rem', fontWeight: 700 }}>{count}</span>
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {unreadCount > 0 && (
              <button onClick={handleMarkAllAsRead} style={{ background: 'rgba(255,255,255,0.94)', border: '1px solid #d8cab5', borderRadius: 8, padding: '11px 13px', fontSize: '0.74rem', color: '#80643d', fontWeight: 900, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Mark read
              </button>
            )}
            {notifications.length > 0 && (
              <button onClick={() => setConfirmClear(true)} style={{ background: 'rgba(255,255,255,0.94)', border: '1px solid #f0cfca', borderRadius: 8, padding: '11px 13px', fontSize: '0.74rem', color: '#b33b2f', fontWeight: 900, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Clear
              </button>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, background: 'rgba(255,255,255,0.92)', border: '1px solid #ded2c1', borderRadius: 8, padding: 6, overflowX: 'auto' }}>
          {FILTER_TYPES.map((item) => {
            const count = item === 'all' ? notifications.length : notifications.filter(n => n.type === item).length;
            const active = filter === item;
            const cfg = TYPE_CONFIG[item] || TYPE_CONFIG.default;
            const label = item === 'all' ? 'All' : (cfg.filterLabel || cfg.label);
            return (
              <button key={item} onClick={() => setFilter(item)} style={{ background: active ? cfg.gradient : 'transparent', color: active ? '#fff' : '#6f604e', border: 'none', borderRadius: 7, padding: '8px 12px', fontSize: '0.74rem', fontWeight: 900, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', boxShadow: active ? `0 12px 24px ${cfg.glow}` : 'none' }}>
                {label}<span style={{ background: active ? 'rgba(255,255,255,0.2)' : '#e8ddd0', color: active ? '#fff' : '#6A7079', borderRadius: 6, padding: '1px 7px', fontSize: '0.68rem', fontWeight: 800, minWidth: 20, textAlign: 'center' }}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ fontSize: '0.86rem', color: '#6f604e', fontWeight: 800 }}>
          {filtered.length} visible
          {filteredOutCount > 0 ? ` • ${filteredOutCount} hidden by filters` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
          <span style={{ padding: '6px 10px', borderRadius: 6, background: '#17110a', color: '#fff', fontSize: '0.7rem', fontWeight: 900, boxShadow: '0 10px 22px rgba(25,17,10,0.14)' }}>{activeTypeLabel}</span>
          <span style={{ padding: '6px 10px', borderRadius: 6, background: 'rgba(255,255,255,0.92)', color: '#80643d', border: '1px solid #e2d7c6', fontSize: '0.7rem', fontWeight: 900 }}>{READ_FILTERS.find((item) => item.key === readFilter)?.label}</span>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '64px 20px',
          background: '#fff',
          borderRadius: 8,
          border: '1px solid #D7DBDF',
        }}>
          <div style={{ fontSize: '3.5rem', marginBottom: 16 }}>🔔</div>
          <h4 style={{ margin: '0 0 8px', fontWeight: 700, color: '#14110d', fontSize: '1.1rem' }}>
            {search ? 'No results match your search'
              : filter === 'all' ? 'Koi notification nahi'
              : `Koi ${filter} notification nahi`}
          </h4>
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#8c7558' }}>
            {search ? 'Doosre keywords try karo ya filter clear karo.'
              : 'Naye orders, enquiries aur appointments yahan real-time mein dikhenge.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {grouped.map(([bucket, items]) => (
            <div key={bucket}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                margin: '0 2px 10px',
              }}>
                <span style={{
                  fontSize: '0.71rem',
                  fontWeight: 900,
                  color: '#6A7079',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                }}>{bucket}</span>
                <span style={{
                  flex: 1, height: 1, background: 'linear-gradient(to right, #dcccb3, transparent)',
                }} />
                <span style={{
                  fontSize: '0.68rem', color: '#b59a72', fontWeight: 800, padding: '4px 8px', borderRadius: 6, background: 'rgba(255,255,255,0.8)', border: '1px solid #e7dbca'
                }}>{items.length}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {items.map(notif => (
                  <NotificationCard
                    key={notif.id}
                    notif={notif}
                    onOpenDetails={handleOpenDetails}
                    onDelete={handleDelete}
                    onToggleRead={handleToggleRead}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <NotificationDetailModal
        notif={selectedNotif}
        onClose={() => setSelectedNotif(null)}
        onNavigate={handleNavigateFromDetails}
        onSendReply={handleSendReply}
      />

      {/* ── CONFIRM CLEAR-ALL MODAL ── */}
      {confirmClear && (
        <div
          onClick={() => setConfirmClear(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(20,17,13,0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
            animation: 'fadeIn 0.18s ease-out',
          }}
        >
          <style>{`@keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }`}</style>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 7,
              padding: 28,
              maxWidth: 420,
              width: '100%',
              boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
              border: '1px solid #D7DBDF',
            }}
          >
            <div style={{
              width: 56, height: 56,
              borderRadius: '50%',
              background: '#fdf0ee',
              border: '1px solid #f5c6c6',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.6rem',
              margin: '0 auto 16px',
            }}>⚠️</div>
            <h3 style={{
              margin: '0 0 8px', textAlign: 'center',
              color: '#14110d', fontSize: '1.15rem', fontWeight: 700,
            }}>Clear all notifications?</h3>
            <p style={{
              margin: '0 0 22px', textAlign: 'center',
              color: '#8c7558', fontSize: '0.9rem', lineHeight: 1.55,
            }}>
              Yeh action permanent hai. {notifications.length} notifications delete ho jayengi aur wapas nahi aayengi.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setConfirmClear(false)}
                style={{
                  flex: 1,
                  background: '#F7F8F9',
                  border: '1px solid #D7DBDF',
                  borderRadius: 7,
                  padding: '11px 16px',
                  fontSize: '0.88rem', fontWeight: 600,
                  color: '#5d5346', cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f0e8da'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#F7F8F9'; }}
              >Cancel</button>
              <button
                onClick={handleClearAll}
                style={{
                  flex: 1,
                  background: '#c0392b',
                  border: '1px solid #c0392b',
                  borderRadius: 7,
                  padding: '11px 16px',
                  fontSize: '0.88rem', fontWeight: 700,
                  color: '#fff', cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#a5281b'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#c0392b'; }}
              >Yes, delete all</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationsPage;
